import { useSendAdminNewsletterEmailMutation } from '@codezo/graphql';
import { Alert, Button, Group, Modal, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconInfoCircle, IconMail, IconSend } from '@tabler/icons-react';
import { useState } from 'react';

interface SendEmailModalProps {
	opened: boolean;
	onClose: () => void;
	onSuccess: () => void;
	profileIds: string[];
	sendToAll?: boolean;
	totalCount?: number;
}

interface SendEmailFormData {
	templateId: string;
	subject: string;
}

export function SendEmailModal({
	opened,
	onClose,
	onSuccess,
	profileIds,
	sendToAll,
	totalCount,
}: SendEmailModalProps) {
	const [loading, setLoading] = useState(false);

	const form = useForm<SendEmailFormData>({
		initialValues: {
			templateId: '',
			subject: '',
		},
		validate: {
			templateId: (value) => (value.trim().length === 0 ? 'Template ID is required' : null),
			subject: (value) =>
				value.trim().length < 3 ? 'Subject must be at least 3 characters' : null,
		},
	});

	const handleClose = () => {
		form.reset();
		setLoading(false);
		onClose();
	};

	const [sendEmail] = useSendAdminNewsletterEmailMutation({
		onCompleted: (data) => {
			if (data.sendAdminNewsletterEmail.success) {
				notifications.show({
					title: 'Emails Sent',
					message:
						data.sendAdminNewsletterEmail.message ||
						`Successfully sent ${data.sendAdminNewsletterEmail.sent} emails`,
					color: 'green',
					icon: <IconCheck size={16} />,
				});
				handleClose();
				onSuccess();
			} else {
				notifications.show({
					title: 'Send Completed With Failures',
					message:
						data.sendAdminNewsletterEmail.message ||
						`Sent ${data.sendAdminNewsletterEmail.sent}, failed ${data.sendAdminNewsletterEmail.failed}`,
					color: 'orange',
				});
				handleClose();
				onSuccess();
			}
		},
		onError: (error) => {
			notifications.show({
				title: 'Send Failed',
				message: error.message || 'An error occurred while sending emails',
				color: 'red',
			});
		},
	});

	const handleSubmit = async () => {
		const validation = form.validate();
		if (validation.hasErrors) return;

		setLoading(true);
		try {
			await sendEmail({
				variables: {
					input: {
						...(sendToAll
							? { sendToAll: true }
							: { profileIds }),
						templateId: form.values.templateId.trim(),
						subject: form.values.subject.trim(),
					},
				},
			});
		} catch {
			// Handled in onError
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			opened={opened}
			onClose={handleClose}
			title={
				<Group gap="sm">
					<IconMail size={20} />
					<Title order={4}>Send Newsletter Email</Title>
				</Group>
			}
			size="md"
			centered
		>
			<Stack gap="md">
				<Alert
					variant="light"
					color="gray"
					icon={<IconInfoCircle size={16} />}
					title={
						sendToAll
							? `All ${totalCount ?? 0} active subscribers selected`
							: `${profileIds.length} recipient${profileIds.length === 1 ? '' : 's'} selected`
					}
				>
					<Text size="sm">
						{sendToAll
							? 'All active subscribers will receive the email. '
							: 'Only subscribers with status ACTIVE will receive the email. '}
						The template HTML will be fetched and dispatched via SendGrid.
					</Text>
				</Alert>

				<TextInput
					label="Template ID"
					placeholder="e.g. abc123"
					{...form.getInputProps('templateId')}
					required
				/>
				<TextInput
					label="Subject"
					placeholder="Email subject"
					{...form.getInputProps('subject')}
					required
				/>

				<Group justify="flex-end" gap="sm" mt="md">
					<Button variant="default" onClick={handleClose} disabled={loading}>
						Cancel
					</Button>
					<Button
						color="dark"
						onClick={handleSubmit}
						loading={loading}
						leftSection={<IconSend size={16} />}
					>
						Send Email
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
}
